import { getRange } from '../../helpers/axisDef';
import { renderXAxisLabels, renderYAxisLabels } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
  TpZoomPanelCoordMapper,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { color } from '../../helpers/colors';
import { getStandardDataColor } from '../../../themes/themes';
import { TpLegendItem, renderLegendItems } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorDataPoint,
  getMappedColorLegendInfo,
} from '../../helpers/colorMappingInfo';
import {
  chartDims,
  convertColumnDataListToDisplayString,
  createSortIndexes,
  generateChartTitle,
  isSelectedItem,
} from '../../helpers/misc';
import {
  TpWebGL2DRenderData,
  TpWebGL2DRenderDataColoredPoints,
  TpWebGL2DRenderDataColoredTriangles,
} from '../../../../components/webgl-2d/interface-renderData';
import { WebGL2DRenderTypes } from '../../../../components/webgl-2d/interfaces';
import { perfTestStart, perfTestStop } from '../../../../data/analyticsDataStorage';
import { TpHoverInfo, TpHoverRenderContext } from '../../zoompan-canvas/ZoomPanCanvas';
import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';
import { clipToRenderedRange } from '../../zoompan-canvas/helpers';

function appendRect(
  gridPosits: number[],
  gridColors: number[],
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number,
  r: number,
  g: number,
  b: number,
  a: number,
) {
  gridPosits.push(xMin, yMin, xMax, yMin, xMax, yMax);
  gridPosits.push(xMin, yMin, xMax, yMax, xMin, yMax);
  for (let i = 0; i < 6; i++) gridColors.push(r, g, b, a);
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);

  const dataTable: TpDataTable = boundData.dataTable;
  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const columnCanonicalTypeInfo = boundData.matrixColumnInfo!.columnCanonicalTypeInfo;
  const matrixColumns = boundData.matrixColumnInfo!.matrixColumns;
  const dataTableColumns = boundData.matrixColumnInfo!.dataTableColumns;
  const dataHoverLabelcolumn = boundData.bindings.hoverLabelColumn.data;
  const matrixColumnsData = boundData.matrixColumnInfo!.matrixColumnsData;
  const getItemIndexById = boundData.getItemIndexById;

  const dataRowItemIds = boundData.bindings.itemId.data;

  const dataColumnLabels = convertColumnDataListToDisplayString(
    boundData.bindings.columnLabelId.columnInfo,
    boundData.bindings.columnLabelId.data,
  );

  const rowCount = dataRowItemIds.length;
  const colCount = matrixColumns.length;

  const [colSortIndex, colSortInvIndex] = createSortIndexes(
    colCount,
    false,
    boundData.bindings.sortColumnBy?.columnInfo,
    boundData.bindings.sortColumnBy?.data,
  );

  let globalMinVal = 1e99;
  let globalMaxVal = -1e99;
  for (const column of matrixColumns) {
    const columnData = boundData.matrixColumnInfo!.matrixColumnsData[column.id];
    const [minValCol, maxValCol] = getRange(columnData, 0.05);
    if (minValCol < globalMinVal) globalMinVal = minValCol;
    if (maxValCol > globalMaxVal) globalMaxVal = maxValCol;
  }

  const opacity = config.opacity;
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, dataColumnLabels, colSortIndex);
  }

  function renderRowLabels(renderContext: TpAreaRenderContext) {
    renderYAxisLabels(renderContext, null, dataColumnLabels, [], undefined, colSortIndex, true);
  }

  function renderMatrix(renderContext: TpAreaRenderContext) {}

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  const legendBottomSizeHeight = chartDims.legendHeight;

  const titleSize = chartDims.headerHeight;
  const rowLabelSize = 80;
  const columnLabelSize = 80;

  const padding = chartDims.padding;

  const hSep = 0.03;

  // Create WebGL background grid
  const gridPosits: number[] = [];
  const gridColors: number[] = [];
  for (let idxX = 0; idxX < colCount; idxX++)
    for (let idxY = 0; idxY < colCount; idxY++) {
      if (idxY >= idxX) {
        appendRect(
          gridPosits,
          gridColors,
          (idxX + hSep) / colCount,
          (idxY + hSep) / colCount,
          (idxX + 1 - hSep) / colCount,
          (idxY + 1 - hSep) / colCount,
          0.95,
          0.95,
          0.95,
          1,
        );
      }
    }

  const renderGridWebGL: TpWebGL2DRenderDataColoredTriangles = {
    layerId: 'grid',
    renderType: WebGL2DRenderTypes.COLORED_TRIANGLES,
    posits: new Float32Array(gridPosits),
    colors: new Float32Array(gridColors),
  };

  // Create WebGL points

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const pointColors = new Float32Array(4 * rowCount);
  const dataColor = getStandardDataColor();
  for (let pointIdx = 0; pointIdx < rowCount; pointIdx++) {
    if (isSelectedItem(selectedItemsMap, dataRowItemIds[pointIdx])) {
      if (colorMappingInfo.hasColors) {
        const color = getMappedColorDataPoint(colorMappingInfo, pointIdx);
        pointColors[4 * pointIdx] = color.r / 255;
        pointColors[4 * pointIdx + 1] = color.g / 255;
        pointColors[4 * pointIdx + 2] = color.b / 255;
      } else {
        pointColors[4 * pointIdx] = dataColor.r / 255;
        pointColors[4 * pointIdx + 1] = dataColor.g / 255;
        pointColors[4 * pointIdx + 2] = dataColor.b / 255;
      }
      pointColors[4 * pointIdx + 3] = opacity;
    } else {
      pointColors[4 * pointIdx] = 0.6;
      pointColors[4 * pointIdx + 1] = 0.6;
      pointColors[4 * pointIdx + 2] = 0.6;
      pointColors[4 * pointIdx + 3] = 0.4;
    }
  }

  const pf = perfTestStart();
  const posits = new Float32Array(2 * rowCount * colCount * colCount);
  const colors = new Float32Array(4 * rowCount * colCount * colCount);
  let ptCount = 0;
  let ofs = 0;
  let ofs2 = 0;
  for (let colIdxX = 0; colIdxX < matrixColumns.length; colIdxX++) {
    const columnX = matrixColumns[colSortIndex[colIdxX]];
    const columnDataX = boundData.matrixColumnInfo!.matrixColumnsData[columnX.id];
    for (let colIdxY = 0; colIdxY < matrixColumns.length; colIdxY++)
      if (colIdxY >= colIdxX) {
        const columnY = matrixColumns[colSortIndex[colIdxY]];
        const columnDataY = boundData.matrixColumnInfo!.matrixColumnsData[columnY.id];
        // mapping formulas:
        // const mX = (colIdxX + hSep + (1 - 2 * hSep) * (xVal - minVal) / (maxVal - minVal)) / colCount;
        // const mY = (colIdxY + 1 - hSep - (1 - 2 * hSep) * (yVal - minVal) / (maxVal - minVal)) / colCount;
        const f1X = (colIdxX + hSep + ((1 - 2 * hSep) * (0 - globalMinVal)) / (globalMaxVal - globalMinVal)) / colCount;
        const f2X = ((1 - 2 * hSep) * 1) / (globalMaxVal - globalMinVal) / colCount;
        const f1Y =
          (colIdxY + 1 - hSep - ((1 - 2 * hSep) * (0 - globalMinVal)) / (globalMaxVal - globalMinVal)) / colCount;
        const f2Y = (-(1 - 2 * hSep) * 1) / (globalMaxVal - globalMinVal) / colCount;

        for (let pointIdx = 0; pointIdx < rowCount; pointIdx++) {
          const xVal = columnDataX[pointIdx];
          const yVal = columnDataY[pointIdx];
          if (!isNaN(xVal) && !isNaN(yVal)) {
            const mX = f1X + f2X * xVal;
            const mY = f1Y + f2Y * yVal;
            posits[2 * ptCount] = mX;
            posits[2 * ptCount + 1] = mY;
            ofs = 4 * ptCount;
            ofs2 = 4 * pointIdx;
            colors[ofs] = pointColors[ofs2];
            colors[ofs + 1] = pointColors[ofs2 + 1];
            colors[ofs + 2] = pointColors[ofs2 + 2];
            colors[ofs + 3] = pointColors[ofs2 + 3];
            ptCount++;
          }
        }
      }
  }
  perfTestStop(pf, 'Correlogram points');

  const renderPointsWebGL: TpWebGL2DRenderDataColoredPoints = {
    layerId: 'pointSel',
    renderType: WebGL2DRenderTypes.COLORED_POINTS,
    posits: posits.slice(0, 2 * ptCount),
    colors: colors.slice(0, 4 * ptCount),
    pointSizeFactor: 2,
  };

  const webGL2DRenderData: TpWebGL2DRenderData = {
    backColor: color(255, 255, 255),
    layers: [renderGridWebGL, renderPointsWebGL],
  };

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(1, 0);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding + rowLabelSize), RenderMaxDiffAbs(padding));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + titleSize),
    RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
  );

  const areaMatrix: TpChartAreaDefinition = {
    id: 'matrix',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'xy',
    render: renderMatrix,
    webGL2DRenderData,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaMatrix,
    {
      id: 'columnLabels',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
        RenderMaxDiffAbs(padding + legendBottomSizeHeight),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'rowLabels',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + rowLabelSize)),
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderRowLabels,
    },
  ];

  chartAreas.push({
    id: 'legend',
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
    renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSizeHeight), RenderMaxDiffAbs(padding)),
    render: renderLegend,
  });

  function customHoverRenderer( // when hovering over a node, highlishts the correspnding points on other charts
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) {
    // we won't render if there are too many charts on display
    const chartCountX = Math.round((mapX.mapInv(mapX.renderMax) - mapX.mapInv(mapX.renderMin)) * colCount);
    const chartCountY = Math.round((mapY.mapInv(mapY.renderMin) - mapY.mapInv(mapY.renderMax)) * colCount);
    if (chartCountX * chartCountY > 50) return;

    clipToRenderedRange(ctx, mapX, mapY);

    if (hoverInfo.dataTableId != dataBinding.dataTableId || !hoverInfo.custom) return;
    if (!hoverInfo.itemId) return;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    if (hoverItemNr === undefined) return;
    //const pf = perfTestStart();
    for (let colIdxX = 0; colIdxX < matrixColumns.length; colIdxX++) {
      const columnX = matrixColumns[colSortIndex[colIdxX]];
      const columnDataX = boundData.matrixColumnInfo!.matrixColumnsData[columnX.id];
      for (let colIdxY = 0; colIdxY < matrixColumns.length; colIdxY++)
        if (colIdxY >= colIdxX) {
          if (
            mapX.map(colIdxX / colCount) < mapX.renderMax &&
            mapX.map((colIdxX + 1) / colCount) > mapX.renderMin &&
            mapY.map(colIdxY / colCount) < mapY.renderMin &&
            mapY.map((colIdxY + 1) / colCount) > mapY.renderMax
          ) {
            const columnY = matrixColumns[colSortIndex[colIdxY]];
            const columnDataY = boundData.matrixColumnInfo!.matrixColumnsData[columnY.id];
            const xVal = columnDataX[hoverItemNr];
            const yVal = columnDataY[hoverItemNr];
            if (!isNaN(xVal) && !isNaN(yVal)) {
              const mX =
                (colIdxX + hSep + ((1 - 2 * hSep) * (xVal - globalMinVal)) / (globalMaxVal - globalMinVal)) / colCount;
              const mY =
                (colIdxY + 1 - hSep - ((1 - 2 * hSep) * (yVal - globalMinVal)) / (globalMaxVal - globalMinVal)) /
                colCount;
              const pX = mapX.map(mX);
              const pY = mapY.map(mY);
              ctx.fillStyle = 'white';
              ctx.fillRect(pX - 2 * pixelRatio, pY + 3 * pixelRatio, 4 * pixelRatio, 10 * pixelRatio);
              ctx.fillRect(pX - 2 * pixelRatio, pY - 13 * pixelRatio, 4 * pixelRatio, 10 * pixelRatio);
              ctx.fillRect(pX - 13 * pixelRatio, pY - 2 * pixelRatio, 10 * pixelRatio, 4 * pixelRatio);
              ctx.fillRect(pX + 3 * pixelRatio, pY - 2 * pixelRatio, 10 * pixelRatio, 4 * pixelRatio);
              ctx.fillStyle = getStandardDataColor().toCanvasString();
              ctx.fillRect(pX - 1 * pixelRatio, pY + 3 * pixelRatio, 2 * pixelRatio, 10 * pixelRatio);
              ctx.fillRect(pX - 1 * pixelRatio, pY - 13 * pixelRatio, 2 * pixelRatio, 10 * pixelRatio);
              ctx.fillRect(pX - 13 * pixelRatio, pY - 1 * pixelRatio, 10 * pixelRatio, 2 * pixelRatio);
              ctx.fillRect(pX + 3 * pixelRatio, pY - 1 * pixelRatio, 10 * pixelRatio, 2 * pixelRatio);
            }
          }
        }
    }
    ctx.restore();
    //perfTestStop(pf, "rendering hover");
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
    columnCanonicalTypeInfo,
    matrixColumns,
    dataTableColumns,
  };

  return {
    definition,
    areaMatrix,
    dataTable,
    dataRowItemIds,
    dataHoverLabel,
    dataColumnLabels,
    dataHoverLabelcolumn,
    columnCanonicalTypeInfo,
    matrixColumns,
    colSortIndex,
    colSortInvIndex,
    getItemIndexById,
    matrixColumnsData,
    hSep,
    globalMinVal,
    globalMaxVal,
    customHoverRenderer,
    colInfoHover: boundData.bindings.hoverLabel?.columnId
      ? getColumnInfo(dataTable, boundData.bindings.hoverLabel?.columnId)
      : null,
    title: generateChartTitle(dataContext, dataBinding, config, ''),
  };
}
